import React, { useState, useMemo, useCallback } from 'react';
import { Typography, Button, Box, useTheme, useMediaQuery, Fade, Grow, CircularProgress, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import { DeviceCard, DevicesTable, DeviceStats } from '../components/devices';
import { SearchBar } from '../components/common';
import DeviceFormModal from '../components/forms/DeviceFormModal';
import DeviceContactsModal from '../components/forms/DeviceContactsModal';
import DeviceTemplateModal from '../components/forms/DeviceTemplateModal';
import { useDevices, useSearchAndFilter, usePageOperations } from '../hooks';
import { THEME_COLORS, BREAKPOINTS, OPERATION_STATUS } from '../constants';
import { useAuth } from '../contexts/AuthContext';

const DevicesPageComponent = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.sm));

  const devicesHook = useDevices();
  const { devices, stats, loading, error, pagination} = devicesHook;

  const {
    searchTerm,
    searchInput,
    searchType,
    handleSearch,
    handleClear: originalHandleClear,
    handleSearchInputChange,
    handleSearchTypeChange
  } = useSearchAndFilter('', 'all', 'device');

  // Función personalizada para limpiar búsqueda y recargar dispositivos
  const handleClear = useCallback(() => {
    originalHandleClear();
    devicesHook.loadDevices(1, pagination.limit);
  }, [originalHandleClear, devicesHook, pagination.limit]);

  const {
    openCreateModal,
    openEditModal,
    openDeleteModal,
    FormModal,
    DeleteModal,
    OperationModal,
    entityToEdit,
  } = usePageOperations({
    entityName: 'Dispositivo',
    entityNamePlural: 'Dispositivos',
    crudFunctions: {
      create: devicesHook.createDevice,
      update: devicesHook.updateDevice,
      delete: devicesHook.deleteDevice,
    },
    FormModalComponent: DeviceFormModal,
    entityIdKey: 'device_id',
  });

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedDeviceForTemplates, setSelectedDeviceForTemplates] = useState(null);
  const [isContactsModalOpen, setIsContactsModalOpen] = useState(false);
  const [deviceForContacts, setDeviceForContacts] = useState(null);

  // Ahora la búsqueda se maneja en el backend, no necesitamos filtrar localmente
  const displayDevices = devices;

  const handleOpenTemplateModal = (device) => {
    setSelectedDeviceForTemplates(device);
    setTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => {
    setTemplateModalOpen(false);
    setSelectedDeviceForTemplates(null);
  };

  const handleSaveTemplates = () => {
    handleCloseTemplateModal();
  };

  const handleOpenContactsModal = useCallback((device) => {
    setDeviceForContacts(device);
    setIsContactsModalOpen(true);
  }, []);

  const handleCloseContactsModal = useCallback(() => {
    setIsContactsModalOpen(false);
    setDeviceForContacts(null);
  }, []);

  const handleSaveContacts = useCallback(() => {
    devicesHook.loadDevices(pagination.page, pagination.limit, searchTerm, searchType);
  }, [devicesHook, pagination.page, pagination.limit, searchTerm, searchType]);

  // Manejar búsqueda directamente en handleSearch en lugar de useEffect
  const handleSearchWithReload = useCallback(() => {
    handleSearch();
    if (searchInput.trim()) {
      devicesHook.loadDevices(1, pagination.limit, searchInput.trim(), searchType);
    }
  }, [handleSearch, searchInput, searchType, devicesHook.loadDevices, pagination.limit]);

  // Función personalizada para manejar cambios de página con búsqueda
  const handlePageChange = useCallback((page) => {
    devicesHook.goToPage(page, searchTerm, searchType);
  }, [devicesHook, searchTerm, searchType]);

  // Función personalizada para manejar cambios de tamaño de página con búsqueda
  const handlePageSizeChange = useCallback((newLimit) => {
    devicesHook.changePageSize(newLimit, searchTerm, searchType);
  }, [devicesHook, searchTerm, searchType]);

  if (error) {
    return (
      <AppLayout currentPage="devices">
        <Box sx={{ paddingY: 4 }}><Typography variant="h6" color="error" align="center">Error al cargar dispositivos: {error}</Typography></Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="devices">
      <Fade in timeout={500}>
        <Box>
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h4" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Gestión de Dispositivos
            </Typography>
            <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: { xs: '0.875rem', sm: '1rem' }, mt: 1 }}>
              Visualiza y administra los dispositivos eWeLink registrados en el sistema.
            </Typography>
          </Box>

          <Grow in timeout={700}>
            <Box>
              <DeviceStats stats={stats} />
            </Box>
          </Grow>

          <Grow in timeout={800}>
            <Box sx={{ my: 4 }}>
              <SearchBar
                searchInput={searchInput}
                searchTerm={searchTerm}
                searchType={searchType}
                onSearchInputChange={handleSearchInputChange}
                onSearch={handleSearchWithReload}
                onClear={handleClear}
                onSearchTypeChange={handleSearchTypeChange}
                showSearchType={true}
              />
            </Box>
          </Grow>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateModal}
              sx={{
                backgroundColor: THEME_COLORS.TEXT_PRIMARY, color: THEME_COLORS.BACKGROUND_PAPER, borderRadius: '12px',
                paddingX: 3, paddingY: 1.5, textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#1a1a1a' }
              }}
            >
              Nuevo Dispositivo
            </Button>
          </Box>

          {loading ? (
            <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />
          ) : (
            <>
              {isMobile ? (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {displayDevices.map(device => (
                    <Grid item xs={12} key={device.device_id}>
                      <DeviceCard
                        device={device}
                        onEdit={() => openEditModal(device)}
                        onDelete={() => openDeleteModal(device)}
                        onConfigureTemplates={handleOpenTemplateModal}
                        onManageContacts={handleOpenContactsModal}
                        canManage
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <DevicesTable
                  devices={displayDevices}
                  onEdit={openEditModal}
                  onDelete={openDeleteModal}
                  onConfigureTemplates={handleOpenTemplateModal}
                  onManageContacts={handleOpenContactsModal}
                  canManage
                  // Props de paginación
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </>
          )}
        </Box>
      </Fade>

      <FormModal />
      <DeleteModal />
      <OperationModal />

      {deviceForContacts && (
        <DeviceContactsModal
          open={isContactsModalOpen}
          onClose={handleCloseContactsModal}
          onSave={handleSaveContacts}
          device={deviceForContacts}
        />
      )}

      {selectedDeviceForTemplates && (
        <DeviceTemplateModal
          open={templateModalOpen}
          onClose={handleCloseTemplateModal}
          device={selectedDeviceForTemplates}
          onSubmit={handleSaveTemplates}
        />
      )}
    </AppLayout>
  );
};

const DevicesPage = React.memo(DevicesPageComponent);
DevicesPage.displayName = 'DevicesPage';

export default DevicesPage;